// Relays the height of the extension panel's window controls from the extension
// (this app's parent frame) to the Addy app nested inside IframeContainer.
//
// The extension draws split / expand / close and "Back to Loan" over the top of the
// panel. It measures how far down they reach and posts HOST_CHROME_INSET_KEY to this
// app; the nested Addy app keeps a band of that height free at its top
// (client: utils/global/hostChrome.ts). Neither can reach the other directly, so this
// relay keeps the latest value, hands it to the nested app whenever it (re)loads, and
// passes the nested app's requests up when it has nothing yet.
//
// No imports on purpose: the extension-panel test harness loads this file directly.

export const HOST_CHROME_INSET_KEY = "addy-host-chrome-inset";
export const HOST_CHROME_INSET_REQUEST_KEY = "addy-get-host-chrome-inset";
export const MAX_HOST_CHROME_INSET_PX = 120;

/** { top } in whole pixels, capped at MAX_HOST_CHROME_INSET_PX, or null if invalid. */
export function normalizeHostChromeInset(value) {
  const top = Number(value?.top);
  if (!Number.isFinite(top) || top < 0) return null;
  return { top: Math.min(Math.round(top), MAX_HOST_CHROME_INSET_PX) };
}

/**
 * @param {object} options
 * @param {Window | null} options.parentWindow The extension panel (window.parent), or null at top level.
 * @param {() => (Window | null)} options.getChildWindow The nested Addy app's window, if mounted.
 */
export function createHostChromeInsetRelay({ parentWindow, getChildWindow }) {
  let latest = null;

  const sendToChild = () => {
    const child = getChildWindow();
    if (latest && child) child.postMessage({ key: HOST_CHROME_INSET_KEY, value: latest }, "*");
  };

  const requestFromParent = () => {
    parentWindow?.postMessage({ key: HOST_CHROME_INSET_REQUEST_KEY }, "*");
  };

  /** Returns true when the message belonged to the relay. */
  const handleMessage = (event) => {
    const key = event?.data?.key;

    if (key === HOST_CHROME_INSET_KEY) {
      if (!parentWindow || event.source !== parentWindow) return false;
      const inset = normalizeHostChromeInset(event.data.value);
      if (inset) {
        latest = inset;
        sendToChild();
      }
      return true;
    }

    if (key === HOST_CHROME_INSET_REQUEST_KEY) {
      const child = getChildWindow();
      if (!child || event.source !== child) return false;
      if (latest) sendToChild();
      else requestFromParent();
      return true;
    }

    return false;
  };

  return {
    handleMessage,
    requestFromParent,
    onChildLoad: sendToChild,
    getLatest: () => latest,
  };
}
